import { LiveTv } from '@mui/icons-material';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSendDeviceCommand } from '~/api/devices';
import {
  ChannelAutocomplete,
  type ChannelAutocomplete__Channel,
} from '~/components/ChannelAutocomplete';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import type { DeviceBulkSelectionInput } from '~/generated/graphql';
import { DeviceCommandKind } from '~/generated/graphql';
import { assert } from '~/lib/assert';

interface TuneChannelDialogProps {
  channelGuideId?: number;
  onClose: () => void;
  onComplete?: () => void;
  open: boolean;
  selected: DeviceBulkSelectionInput;
}

export const TuneChannelDialog = ({
  channelGuideId,
  onClose,
  onComplete,
  open,
  selected,
}: TuneChannelDialogProps) => {
  const { t } = useTranslation();

  const [selectedChannel, setSelectedChannel] = useState<ChannelAutocomplete__Channel | null>(null);

  const [sendCommand, { called }] = useSendDeviceCommand();

  const tune = () => {
    assert(selectedChannel != null, 'TuneChannelDialog: No channel selected');
    void sendCommand({
      variables: {
        input: {
          command: DeviceCommandKind.TuneChannel,
          payload: { id: selectedChannel.id },
          selected,
        },
      },
    });
    onComplete?.();
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
      <DialogTitle onClose={onClose}>{t('pages.devices.dialogs.tune_channel.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>
          {t('pages.devices.dialogs.tune_channel.description')}
        </DialogContentTitle>

        <ChannelAutocomplete
          channelGuideId={channelGuideId}
          onChange={setSelectedChannel}
          value={selectedChannel}
        />
      </DialogContent>

      <DialogActions>
        <Button disabled={called} onClick={onClose} variant="outlined">
          {t('common.actions.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={selectedChannel == null || called}
          onClick={tune}
          startIcon={<LiveTv />}
          variant="contained"
        >
          {t('pages.devices.dialogs.tune_channel.tune_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
